import type {
  EquipmentLinkInstanceInterface,
  EquipmentLinkInterface,
  EquipmentNodeLinkInstanceInterface,
  EquipmentLinkRequest,
  OptimizedEquipmentLinkInterface,
} from '@interfaces/equipment'
import type {
  EquipmentLinkTypeInterface,
  EquipmentLinkTypeRequest,
} from '@interfaces/equipmentLinkType'
import type {EquipmentGroupInterface} from '@interfaces/equipmentGroup'
import type {Floor} from '@interfaces/floors'
import type {MachineSubSystemType} from '@interfaces/machineSubsystemType'
import type {ProductionArea} from '@interfaces/productionAreas'
import type {ProductionSite} from '@interfaces/productionSite'
import type {ProgramClassificationInterface} from '@interfaces/programClassification'
import type {
  ResourceGroupAssignableResourceKey,
  ResourceGroupAssignableResourceType,
  ResourceGroupAssignmentResponse,
  ResourceGroupInterface,
} from '@interfaces/resourceGroup'
import type {ProgramInterface} from '@interfaces/program'
import type {SignalTypeInterface} from '@interfaces/signalType'
import {baseApi} from './base'
import type {BaseFetchResultsInterface} from '@interfaces/base.ts'

interface PaginationSearchPromptsArgs {
  limit?: number
  offset?: number
  search?: string
  ordering?: string
}

const buildParams = (
  entries: Record<string, string | number | boolean | undefined | null>,
): string => {
  const params = new URLSearchParams()
  Object.entries(entries).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.set(key, String(value))
    }
  })
  const str = params.toString()
  return str ? `?${str}` : ''
}

interface GetEquipmentLinkInstancesArgs extends PaginationSearchPromptsArgs {
  equipmentLinkId: string
}

interface GetEquipmentNodeInstancesArgs extends PaginationSearchPromptsArgs {
  equipmentId: string
  direction?: 'incoming' | 'outgoing' | 'both'
}

interface UpdateResourceGroupRequest {
  id: string
  data: Partial<ResourceGroupInterface>
}

interface AssignResourceGroupRequest {
  resourceType: ResourceGroupAssignableResourceType
  resourceId: string
  resourceGroupId: string
}

const resourceGroupAssignmentUrlByType: Record<ResourceGroupAssignableResourceType, string> = {
  site: 'production-sites',
  area: 'production-areas',
  line: 'machines',
  equipment: 'machinesubsystems',
}

const resourceGroupAssignmentTagByType: Record<
  ResourceGroupAssignableResourceType,
  'ProductionSite' | 'ProductionArea' | 'Line' | 'Equipment'
> = {
  site: 'ProductionSite',
  area: 'ProductionArea',
  line: 'Line',
  equipment: 'Equipment',
}

// Assigning a resource to a group (or editing membership) can shift any of the
// resource lists, so they're all invalidated together.
const resourceGroupMemberListTags = [
  {type: 'ProductionSite' as const, id: 'LIST' as const},
  {type: 'ProductionArea' as const, id: 'LIST' as const},
  {type: 'Line' as const, id: 'LIST' as const},
  {type: 'Equipment' as const, id: 'LIST' as const},
]

// One page of the resource-group listing when walking it to completion.
const RESOURCE_GROUP_PAGE_SIZE = 200

const settingsAPI = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getResourceGroups: builder.query<
      BaseFetchResultsInterface<ResourceGroupInterface>,
      {limit?: number; ordering?: string; offset?: number; search?: string}
    >({
      query: ({limit = 100, ordering = 'name', offset = 0, search}) => ({
        url: 'resource-groups/',
        params: {limit, offset, ordering, ...(search ? {search} : {})},
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({id}) => ({
                type: 'ResourceGroup' as const,
                id,
              })),
              {type: 'ResourceGroup' as const, id: 'LIST'},
            ]
          : [{type: 'ResourceGroup' as const, id: 'LIST'}],
    }),
    /**
     * EVERY resource group granting this team access, across as many pages as it
     * takes.
     *
     * Deliberately not a page of `getResourceGroups`. This feeds the assigned
     * side of a dual-pane picker, which renders its rows in full and has no
     * paging of its own — so a capped fetch would be the very defect the picker
     * was fixed for: a group past the cap would read as unassigned, and the next
     * save would drop the team from it.
     *
     * The server-side `?team=` filter is what makes this cheap: a team belongs to
     * a handful of groups, so this is normally one request, and the loop only
     * exists so "a handful" is not an assumption the UI depends on.
     */
    getResourceGroupsForTeam: builder.query<{results: ResourceGroupInterface[]}, {team: string}>({
      async queryFn({team}, _queryApi, _extraOptions, fetchWithBQ) {
        const results: ResourceGroupInterface[] = []
        let offset = 0
        for (;;) {
          // Sequential on purpose: each request's offset depends on the previous
          // page, and there is no count to parallelise from until the first
          // response lands.
          // eslint-disable-next-line no-await-in-loop
          const response = await fetchWithBQ({
            url: 'resource-groups/',
            params: {limit: RESOURCE_GROUP_PAGE_SIZE, offset, ordering: 'name', team},
          })
          if (response.error) {
            return {error: response.error}
          }
          const page = response.data as BaseFetchResultsInterface<ResourceGroupInterface>
          const rows = page.results || []
          results.push(...rows)
          // Stop on a short/empty page as well as on the count, so a server that
          // disagrees with its own `count` cannot spin this loop.
          if (!rows.length || results.length >= (page.count ?? results.length)) {
            break
          }
          offset += RESOURCE_GROUP_PAGE_SIZE
        }
        return {data: {results}}
      },
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({id}) => ({type: 'ResourceGroup' as const, id})),
              {type: 'ResourceGroup' as const, id: 'LIST'},
            ]
          : [{type: 'ResourceGroup' as const, id: 'LIST'}],
    }),
    getResourceGroup: builder.query<ResourceGroupInterface, {id: string}>({
      query: ({id}) => ({url: `resource-groups/${id}/`}),
      providesTags: (_result, _error, {id}) => [{type: 'ResourceGroup' as const, id}],
    }),
    createResourceGroup: builder.mutation<ResourceGroupInterface, Partial<ResourceGroupInterface>>({
      query: (newResourceGroup) => ({
        url: 'resource-groups/',
        method: 'POST',
        body: newResourceGroup,
      }),
      invalidatesTags: [{type: 'ResourceGroup' as const, id: 'LIST'}],
    }),
    updateResourceGroup: builder.mutation<ResourceGroupInterface, UpdateResourceGroupRequest>({
      query: ({id, data}) => ({
        url: `resource-groups/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, {id}) => [
        {type: 'ResourceGroup' as const, id},
        {type: 'ResourceGroup' as const, id: 'LIST'},
      ],
    }),
    deleteResourceGroup: builder.mutation<void, {id: string}>({
      query: ({id}) => ({
        url: `resource-groups/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, {id}) => [
        {type: 'ResourceGroup' as const, id},
        {type: 'ResourceGroup' as const, id: 'LIST'},
      ],
    }),
    assignResourceGroup: builder.mutation<
      ResourceGroupAssignmentResponse,
      AssignResourceGroupRequest
    >({
      query: ({resourceType, resourceId, resourceGroupId}) => ({
        url: `${resourceGroupAssignmentUrlByType[resourceType]}/${resourceId}/assign-resource-group/`,
        method: 'POST',
        body: {resource_group: resourceGroupId},
      }),
      invalidatesTags: (_result, _error, {resourceType, resourceId, resourceGroupId}) => [
        {type: 'ResourceGroup' as const, id: resourceGroupId},
        {type: 'ResourceGroup' as const, id: 'LIST'},
        // The assigned resource itself moved groups...
        {type: resourceGroupAssignmentTagByType[resourceType], id: resourceId},
        // ...and its list shifts. The resource list queries provide a BARE type
        // tag (id: undefined), so invalidate the bare type here — an id:'LIST' tag
        // would not match them.
        resourceGroupAssignmentTagByType[resourceType],
      ],
    }),
    /**
     * The downward closure of the given seed ids, per resource type.
     *
     * `set-members` reconciles a FULL selection per level: it expands the seeds
     * it is given, so ADDING a parent cascades server-side, but a descendant
     * left in the payload counts as independently selected and survives. So a
     * client that cannot enumerate a subtree cannot express a REMOVAL —
     * deselecting a line used to leave its off-page equipment in the group.
     * Asking here is what lets the resource panes page instead of loading every
     * resource just to build a parent → children index.
     */
    getResourceGroupSubtree: builder.query<
      Record<ResourceGroupAssignableResourceKey, string[]>,
      {
        resourceGroupId: string
        sites?: string[]
        areas?: string[]
        lines?: string[]
        equipment?: string[]
      }
    >({
      query: ({resourceGroupId, sites, areas, lines, equipment}) => ({
        url: `resource-groups/${resourceGroupId}/subtree/`,
        params: {
          ...(sites?.length ? {sites: sites.join(',')} : {}),
          ...(areas?.length ? {areas: areas.join(',')} : {}),
          ...(lines?.length ? {lines: lines.join(',')} : {}),
          ...(equipment?.length ? {equipment: equipment.join(',')} : {}),
        },
      }),
    }),
    setResourceGroupMembers: builder.mutation<
      {
        message: string
        data: Record<string, {added: string[]; removed: string[]; total: number}>
      },
      {
        resourceGroupId: string
        body: {sites?: string[]; areas?: string[]; lines?: string[]; equipment?: string[]}
      }
    >({
      query: ({resourceGroupId, body}) => ({
        url: `resource-groups/${resourceGroupId}/set-members/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, {resourceGroupId}) => [
        {type: 'ResourceGroup' as const, id: resourceGroupId},
        {type: 'ResourceGroup' as const, id: 'LIST'},
        ...resourceGroupMemberListTags,
      ],
    }),
    getMachineSubsystemTypes: builder.query<
      {
        count: number
        next: string | null
        previous: string | null
        results: MachineSubSystemType[]
      },
      {limit?: number; ordering?: string; offset?: number; search?: string}
    >({
      query: ({limit, ordering, offset, search}) =>
        `machinesubsystemtypes/${buildParams({limit, offset, ordering, search})}`,
      providesTags: ['MachineSubSystemType'],
    }),
    getSignalTypes: builder.query<
      {count: number; next: string | null; previous: string | null; results: SignalTypeInterface[]},
      {limit?: number; ordering?: string; offset?: number; search?: string}
    >({
      query: ({limit, ordering, offset, search}) =>
        `signaltypes/${buildParams({limit, offset, ordering, search})}`,
      providesTags: ['SignalType'],
    }),
    createMachineSubsystemType: builder.mutation<
      MachineSubSystemType,
      Partial<MachineSubSystemType>
    >({
      query: (newSubsystem) => ({
        url: 'machinesubsystemtypes/',
        method: 'POST',
        body: newSubsystem,
      }),
      invalidatesTags: ['MachineSubSystemType'],
    }),
    updateMachineSubsystemType: builder.mutation<
      MachineSubSystemType,
      {id: string; data: Partial<MachineSubSystemType>}
    >({
      query: ({id, data}) => ({
        url: `machinesubsystemtypes/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['MachineSubSystemType'],
    }),
    deleteMachineSubsystemType: builder.mutation<{message: string}, {id: string}>({
      query: ({id}) => ({
        url: `machinesubsystemtypes/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['MachineSubSystemType'],
    }),
    createSignalType: builder.mutation<SignalTypeInterface, Partial<SignalTypeInterface>>({
      query: (newSignalType) => ({
        url: 'signaltypes/',
        method: 'POST',
        body: newSignalType,
      }),
      invalidatesTags: ['SignalType'],
    }),
    updateSignalType: builder.mutation<
      SignalTypeInterface,
      {id: string; data: Partial<SignalTypeInterface>}
    >({
      query: ({id, data}) => ({
        url: `signaltypes/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['SignalType'],
    }),
    deleteSignalType: builder.mutation<{message: string}, {id: string}>({
      query: ({id}) => ({
        url: `signaltypes/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SignalType'],
    }),
    getProgramClassifications: builder.query<
      {
        count: number
        next: string | null
        previous: string | null
        results: ProgramClassificationInterface[]
      },
      {limit?: number; ordering?: string; offset?: number; search?: string}
    >({
      query: ({limit = 100, ordering, offset = 0, search}) =>
        `programclassifications/${buildParams({limit, offset, ordering, search})}`,
      providesTags: ['ProgramClassification'],
    }),
    createProgramClassification: builder.mutation<
      ProgramClassificationInterface,
      Partial<ProgramClassificationInterface>
    >({
      query: (newClassification) => ({
        url: 'programclassifications/',
        method: 'POST',
        body: newClassification,
      }),
      invalidatesTags: ['ProgramClassification'],
    }),
    updateProgramClassification: builder.mutation<
      ProgramClassificationInterface,
      {id: string; data: Partial<ProgramClassificationInterface>}
    >({
      query: ({id, data}) => ({
        url: `programclassifications/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['ProgramClassification'],
    }),
    deleteProgramClassification: builder.mutation<{message: string}, {id: string}>({
      query: ({id}) => ({
        url: `programclassifications/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ProgramClassification'],
    }),
    getProductionAreas: builder.query<
      {count: number; next: string | null; previous: string | null; results: ProductionArea[]},
      {limit?: number; ordering?: string; offset?: number; search?: string; scoped?: boolean}
    >({
      query: ({limit, ordering, offset, search, scoped = true}) =>
        `production-areas/${buildParams({limit, offset, ordering, search, scoped})}`,
      providesTags: ['ProductionArea'],
    }),
    createProductionArea: builder.mutation<ProductionArea, Partial<ProductionArea>>({
      query: (newProductionArea) => ({
        url: 'production-areas/',
        method: 'POST',
        body: newProductionArea,
      }),
      invalidatesTags: ['ProductionArea'],
    }),
    updateProductionArea: builder.mutation<
      ProductionArea,
      {id: string; data: Partial<ProductionArea>}
    >({
      query: ({id, data}) => ({
        url: `production-areas/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['ProductionArea'],
    }),
    deleteProductionArea: builder.mutation<{message: string}, {id: string}>({
      query: ({id}) => ({
        url: `production-areas/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ProductionArea'],
    }),
    getEquipmentGroups: builder.query<
      BaseFetchResultsInterface<EquipmentGroupInterface>,
      {limit?: number; ordering?: string; offset?: number; search?: string}
    >({
      query: ({limit, ordering, offset, search}) =>
        `equipment-groups/${buildParams({limit, offset, ordering, search})}`,
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({id}) => ({type: 'EquipmentGroup' as const, id})),
              {type: 'EquipmentGroup' as const, id: 'LIST'},
            ]
          : [{type: 'EquipmentGroup' as const, id: 'LIST'}],
    }),
    getEquipmentGroup: builder.query<EquipmentGroupInterface, {id: string}>({
      query: ({id}) => `equipment-groups/${id}/`,
      providesTags: (_result, _error, {id}) => [{type: 'EquipmentGroup' as const, id}],
    }),
    createEquipmentGroup: builder.mutation<
      EquipmentGroupInterface,
      Partial<Omit<EquipmentGroupInterface, 'equipments'>> & {equipments?: string[]}
    >({
      query: (newEquipmentGroup) => ({
        url: 'equipment-groups/',
        method: 'POST',
        body: newEquipmentGroup,
      }),
      invalidatesTags: [{type: 'EquipmentGroup' as const, id: 'LIST'}],
    }),
    updateEquipmentGroup: builder.mutation<
      EquipmentGroupInterface,
      {
        id: string
        data: Partial<Omit<EquipmentGroupInterface, 'equipments'>> & {equipments?: string[]}
      }
    >({
      query: ({id, data}) => ({
        url: `equipment-groups/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, {id}) => [{type: 'EquipmentGroup' as const, id}],
    }),
    deleteEquipmentGroup: builder.mutation<{message: string}, {id: string}>({
      query: ({id}) => ({
        url: `equipment-groups/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{type: 'EquipmentGroup' as const, id: 'LIST'}],
    }),
    getFloors: builder.query<
      {count: number; next: string | null; previous: string | null; results: Floor[]},
      {limit?: number; ordering?: string; offset?: number; search?: string; productionSite?: string}
    >({
      query: ({limit, ordering, offset, search, productionSite}) =>
        `floors/${buildParams({
          limit,
          offset,
          ordering,
          search,
          production_site: productionSite,
        })}`,
      providesTags: ['Floor'],
    }),
    createFloor: builder.mutation<Floor, Partial<Floor>>({
      query: (newFloor) => ({
        url: 'floors/',
        method: 'POST',
        body: newFloor,
      }),
      invalidatesTags: ['Floor'],
    }),
    updateFloor: builder.mutation<Floor, {id: string; data: Partial<Floor>}>({
      query: ({id, data}) => ({
        url: `floors/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Floor'],
    }),
    deleteFloor: builder.mutation<{message: string}, {id: string}>({
      query: ({id}) => ({
        url: `floors/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Floor'],
    }),
    getProductionSites: builder.query<
      {count: number; next: string | null; previous: string | null; results: ProductionSite[]},
      {limit?: number; ordering?: string; offset?: number; search?: string}
    >({
      query: ({limit, ordering, offset, search}) =>
        `production-sites/${buildParams({limit, offset, ordering, search})}`,
      providesTags: ['ProductionSite'],
    }),
    createProductionSite: builder.mutation<ProductionSite, Partial<ProductionSite>>({
      query: (newProductionSite) => ({
        url: 'production-sites/',
        method: 'POST',
        body: newProductionSite,
      }),
      invalidatesTags: ['ProductionSite'],
    }),

    updateProductionSite: builder.mutation<
      ProductionSite,
      {id: string; data: Partial<ProductionSite>}
    >({
      query: ({id, data}) => ({
        url: `production-sites/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['ProductionSite'],
    }),
    deleteProductionSite: builder.mutation<{message: string}, {id: string}>({
      query: ({id}) => ({
        url: `production-sites/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ProductionSite'],
    }),

    // Equipment Link endpoints
    getEquipmentLinks: builder.query<
      {
        count: number
        next: string | null
        previous: string | null
        results: EquipmentLinkInterface[]
      },
      {limit?: number; ordering?: string; offset?: number; search?: string; all?: boolean}
    >({
      query: ({limit, ordering, offset, search, all}) =>
        `equipment-links/${buildParams({...(all ? {all: true} : {limit, offset}), ordering, search})}`,
      providesTags: ['EquipmentLink'],
    }),

    getEquipmentLinksByMachine: builder.query<EquipmentLinkInterface[], {machine_id: string}>({
      query: ({machine_id}) => `equipment-links/by_machine/?machine_id=${machine_id}`,
      providesTags: ['EquipmentLink'],
    }),

    getEquipmentLinksByEquipment: builder.query<EquipmentLinkInterface[], {equipment_id: string}>({
      query: ({equipment_id}) => `equipment-links/by_equipment/?equipment_id=${equipment_id}`,
      providesTags: ['EquipmentLink'],
    }),

    getEquipmentLinkById: builder.query<EquipmentLinkInterface, {id: string}>({
      query: ({id}) => `equipment-links/${id}/`,
      providesTags: ['EquipmentLink'],
    }),

    getEquipmentLinksForDigitalTwin: builder.query<
      {nodes: unknown[]; links: unknown[]},
      {machine_id: string; site_id?: string; area_id?: string; line_id?: string}
    >({
      query: ({machine_id, site_id, area_id, line_id}) => {
        let queryString = `equipment-links/for_digital_twin/?machine_id=${machine_id}`
        if (site_id) {
          queryString += `&site_id=${site_id}`
        }
        if (area_id) {
          queryString += `&area_id=${area_id}`
        }
        if (line_id) {
          queryString += `&line_id=${line_id}`
        }
        return queryString
      },
      providesTags: ['EquipmentLink', 'Program'],
    }),

    getEquipmentLinksForGraph: builder.query<
      OptimizedEquipmentLinkInterface[],
      {machine_id?: string; all?: boolean}
    >({
      query: ({machine_id, all}) => {
        const queryString = `equipment-links/for_graph/`
        const params = new URLSearchParams()

        if (machine_id) {
          params.append('machine_id', machine_id)
        }
        if (all === false) {
          params.append('all', 'false')
        }

        const queryParams = params.toString()
        return queryParams ? `${queryString}?${queryParams}` : queryString
      },
      providesTags: ['EquipmentLink'],
    }),

    getProgramsForDigitalTwin: builder.query<
      ProgramInterface[],
      {machine_id: string; site_id?: string; area_id?: string; line_id?: string}
    >({
      query: ({machine_id, site_id, area_id, line_id}) => {
        let queryString = `programs/?machine=${machine_id}&target_machine_sub_system__isnull=false`
        if (site_id) {
          queryString += `&site=${site_id}`
        }
        if (area_id) {
          queryString += `&area=${area_id}`
        }
        if (line_id) {
          queryString += `&line=${line_id}`
        }
        return queryString
      },
      transformResponse: (response: {results: ProgramInterface[]}) => response.results,
      providesTags: ['Program', 'Trigger'],
    }),

    getEquipmentLinkInstances: builder.query<
      BaseFetchResultsInterface<EquipmentLinkInstanceInterface>,
      GetEquipmentLinkInstancesArgs
    >({
      query: ({equipmentLinkId, ...params}) => ({
        url: `equipment_links/${equipmentLinkId}/instances/`,
        method: 'GET',
        params: params,
      }),
      providesTags: ['EquipmentLinkInstance'],
    }),
    getEquipmentNodeInstances: builder.query<
      BaseFetchResultsInterface<EquipmentNodeLinkInstanceInterface>,
      GetEquipmentNodeInstancesArgs
    >({
      query: ({equipmentId, ...params}) => ({
        url: `equipment_links/by_equipment/${equipmentId}/instances/`,
        method: 'GET',
        params: params,
      }),
      providesTags: ['EquipmentLinkInstance'],
    }),
    // Read-only pre-fill for the instance recompute offered after a link change (AB#6595):
    // the oldest batch of the link's programs, and how many instances that date would recompute.
    getLinkInstanceRecomputeSuggestion: builder.query<
      {suggested_from: string | null; link_instances_affected: number},
      {equipmentLinkId: string; date?: string}
    >({
      query: ({equipmentLinkId, date}) => ({
        url: `equipment_links/${equipmentLinkId}/instances/recompute_suggestion/`,
        params: date ? {date} : {},
      }),
      keepUnusedDataFor: 0,
    }),
    resetEquipmentLinkInstances: builder.mutation<
      {ok: boolean},
      {equipmentLinkId: string; date: string}
    >({
      query: ({equipmentLinkId, date}) => ({
        url: `equipment_links/${equipmentLinkId}/instances/reset/`,
        method: 'POST',
        body: {date},
      }),
      invalidatesTags: ['EquipmentLinkInstance', 'EquipmentLink'],
    }),
    // Reset the dynamic ELI schedule of an equipment link back to the default (1 min). The next
    // weekly recompute reverts it to the heuristic interval (AB#5386). 200 on success; the backend
    // returns 400 when not allowed (custom-pinned / ineligible). Links have no "disabled" concept,
    // so there is no 409 branch.
    resetEquipmentLinkSchedule: builder.mutation<
      {result: {id: string; eli_interval_minutes: number}},
      {equipmentLinkId: string}
    >({
      query: ({equipmentLinkId}) => ({
        url: `equipment-links/${equipmentLinkId}/reset_schedule/`,
        method: 'POST',
      }),
      // Optimistically set the interval to 1 min on the cached link so the UI updates instantly;
      // roll back if the request fails. A successful run also leaves the cache at 1 min.
      onQueryStarted: async ({equipmentLinkId}, {dispatch, queryFulfilled}) => {
        const patch = dispatch(
          settingsAPI.util.updateQueryData(
            'getEquipmentLinkById',
            {id: equipmentLinkId},
            (draft) => {
              draft.eli_interval_minutes = 1
            },
          ),
        )
        try {
          const {
            data: {result},
          } = await queryFulfilled
          if (typeof result?.eli_interval_minutes === 'number') {
            dispatch(
              settingsAPI.util.updateQueryData(
                'getEquipmentLinkById',
                {id: equipmentLinkId},
                (draft) => {
                  draft.eli_interval_minutes = result.eli_interval_minutes
                },
              ),
            )
          }
        } catch {
          patch.undo()
        }
      },
    }),

    createEquipmentLink: builder.mutation<EquipmentLinkInterface, EquipmentLinkRequest>({
      query: (newEquipmentLink) => ({
        url: 'equipment-links/',
        method: 'POST',
        body: newEquipmentLink,
      }),
      invalidatesTags: ['EquipmentLink'],
    }),

    updateEquipmentLink: builder.mutation<
      EquipmentLinkInterface,
      {id: string; data: Partial<EquipmentLinkRequest>}
    >({
      query: ({id, data}) => ({
        url: `equipment-links/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['EquipmentLink'],
    }),

    deleteEquipmentLink: builder.mutation<{message: string}, {id: string}>({
      query: ({id}) => ({
        url: `equipment-links/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['EquipmentLink'],
    }),

    uploadEquipmentLinkExcel: builder.mutation<
      {message: string; data: EquipmentLinkInterface[]; warnings?: string[]},
      {file: FormData}
    >({
      query: ({file}) => ({
        url: `equipment-links/upload-excel/`,
        method: 'POST',
        body: file,
      }),
      invalidatesTags: ['EquipmentLink'],
    }),

    // Equipment Link Type (material/product) endpoints
    getEquipmentLinkTypes: builder.query<
      {
        count: number
        next: string | null
        previous: string | null
        results: EquipmentLinkTypeInterface[]
      },
      {limit?: number; ordering?: string; offset?: number; search?: string; all?: boolean}
    >({
      query: ({limit, ordering, offset, search, all}) =>
        `equipment-link-types/${buildParams({...(all ? {all: true} : {limit, offset}), ordering, search})}`,
      providesTags: ['EquipmentLinkType'],
    }),
    createEquipmentLinkType: builder.mutation<EquipmentLinkTypeInterface, EquipmentLinkTypeRequest>(
      {
        query: (body) => ({
          url: 'equipment-link-types/',
          method: 'POST',
          body,
        }),
        invalidatesTags: ['EquipmentLinkType'],
      },
    ),
    updateEquipmentLinkType: builder.mutation<
      EquipmentLinkTypeInterface,
      {id: string; data: Partial<EquipmentLinkTypeRequest>}
    >({
      query: ({id, data}) => ({
        url: `equipment-link-types/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['EquipmentLinkType', 'EquipmentLink'],
    }),
    deleteEquipmentLinkType: builder.mutation<{message: string}, {id: string}>({
      query: ({id}) => ({
        url: `equipment-link-types/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['EquipmentLinkType', 'EquipmentLink'],
    }),
  }),
})

export const {
  useAssignResourceGroupMutation,
  useSetResourceGroupMembersMutation,
  useLazyGetResourceGroupSubtreeQuery,
  useCreateResourceGroupMutation,
  useDeleteResourceGroupMutation,
  useGetResourceGroupQuery,
  useLazyGetResourceGroupQuery,
  useGetResourceGroupsQuery,
  useGetResourceGroupsForTeamQuery,
  useLazyGetResourceGroupsQuery,
  useUpdateResourceGroupMutation,
  useGetMachineSubsystemTypesQuery,
  useCreateMachineSubsystemTypeMutation,
  useUpdateMachineSubsystemTypeMutation,
  useDeleteMachineSubsystemTypeMutation,
  useGetProductionAreasQuery,
  useLazyGetProductionAreasQuery,
  useCreateProductionAreaMutation,
  useUpdateProductionAreaMutation,
  useDeleteProductionAreaMutation,
  useGetEquipmentGroupsQuery,
  useLazyGetEquipmentGroupsQuery,
  useGetEquipmentGroupQuery,
  useCreateEquipmentGroupMutation,
  useUpdateEquipmentGroupMutation,
  useDeleteEquipmentGroupMutation,
  useGetFloorsQuery,
  useLazyGetFloorsQuery,
  useCreateFloorMutation,
  useUpdateFloorMutation,
  useDeleteFloorMutation,
  useGetProductionSitesQuery,
  useLazyGetProductionSitesQuery,
  useCreateProductionSiteMutation,
  useUpdateProductionSiteMutation,
  useDeleteProductionSiteMutation,
  useGetSignalTypesQuery,
  useCreateSignalTypeMutation,
  useUpdateSignalTypeMutation,
  useDeleteSignalTypeMutation,
  useGetProgramClassificationsQuery,
  useCreateProgramClassificationMutation,
  useUpdateProgramClassificationMutation,
  useDeleteProgramClassificationMutation,
  useGetEquipmentLinksQuery,
  useGetEquipmentLinksByMachineQuery,
  useGetEquipmentLinksByEquipmentQuery,
  useGetEquipmentLinkInstancesQuery,
  useGetEquipmentNodeInstancesQuery,
  useResetEquipmentLinkInstancesMutation,
  useLazyGetLinkInstanceRecomputeSuggestionQuery,
  useGetLinkInstanceRecomputeSuggestionQuery,
  useResetEquipmentLinkScheduleMutation,
  useGetEquipmentLinkByIdQuery,
  useGetEquipmentLinksForDigitalTwinQuery,
  useGetEquipmentLinksForGraphQuery,
  useGetProgramsForDigitalTwinQuery,
  useCreateEquipmentLinkMutation,
  useUpdateEquipmentLinkMutation,
  useDeleteEquipmentLinkMutation,
  useUploadEquipmentLinkExcelMutation,
  useGetEquipmentLinkTypesQuery,
  useCreateEquipmentLinkTypeMutation,
  useUpdateEquipmentLinkTypeMutation,
  useDeleteEquipmentLinkTypeMutation,
} = settingsAPI
